import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import useThemeColor from "../../hooks/useThemeColor.js";
import { logout } from "../../app/auth/authSlice.js";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import { isSmsOnlyPackage } from "../smsPackages/smsPackageHelpers.js";

const ManagePackages = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeColor = useThemeColor();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusConfirmPackage, setStatusConfirmPackage] = useState(null);

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_PACKAGES_WITH_PRICES);

      if (response.data.status) {
        const packageData = response.data.data;
        if (Array.isArray(packageData)) {
          // Subscription packages only — SMS top-ups live under SMS Packages
          setPackages(packageData.filter((pkg) => !isSmsOnlyPackage(pkg)));
        } else {
          setPackages([]);
        }
      } else {
        showErrorToast(response.data.message || "Failed to fetch packages");
        setPackages([]);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to fetch packages");
      }
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleAddPackage = () => {
    navigate("/packages/addPackage");
  };

  const handleEditPackage = (pkg) => {
    navigate(`/packages/updatePackage/${pkg.id}`);
  };

  const handleDeletePackage = async (packageId) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "This will deactivate the package. Companies with active subscriptions will not be affected.",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_PACKAGE, {
          params: { packageID: packageId },
        });

        if (response.data.status) {
          showSuccessToast("Package deleted successfully");
          fetchPackages();
        } else {
          showErrorToast(response.data.message || "Failed to delete package");
        }
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to delete package");
      }
    }
  };

  const confirmPackageStatusChange = async () => {
    if (!statusConfirmPackage) return;

    const { id, pStatus, pName } = statusConfirmPackage;
    const newStatus = !pStatus;

    setStatusConfirmPackage(null);

    try {
      setLoading(true);
      const response = await axiosInstance.patch(
        APIs.PATCH_UPDATE_PACKAGE_STATUS,
        null,
        {
          params: {
            packageID: id,
            status: newStatus,
          },
        }
      );

      if (response.data.status) {
        showSuccessToast(
          `"${pName || "Package"}" is now ${newStatus ? "Active" : "InActive"}.`
        );
        fetchPackages();
      } else {
        showErrorToast(response.data.message || "Failed to update status");
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to update package status");
    }
  };

  const formatPriceSummary = (pkg) => {
    const prices = Array.isArray(pkg.prices) ? pkg.prices : [];
    if (prices.length === 0) return "-";

    const p = prices[0];
    const d = parseInt(p.duration, 10) || 1;
    const months =
      d === 30 || d === 31
        ? 1
        : d === 365 || d === 366
          ? 12
          : d > 24
            ? Math.max(1, Math.round(d / 30))
            : d;
    const amount = parseFloat(p.price || 0).toFixed(2);
    const vat = parseFloat(pkg.pVatPercent ?? 0);
    const vatPart = vat > 0 ? ` + ${vat}% VAT` : "";
    return `£${amount} / ${months} mo${vatPart}`;
  };

  return (
    <div className="content">
      {loading && <Preloader />}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Manage Packages</li>
                </ol>
                <h1 className="page-header mb-0">Subscription Packages</h1>
              </div>
              <div className="ms-auto">
                <button
                  className="btn px-4"
                  onClick={handleAddPackage}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: "none",
                    color: "white",
                    borderRadius: "25px",
                    fontWeight: "500",
                  }}
                >
                  <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
                  Add Package
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="bg-light">
                      <tr>
                        <th className="pt-2 pb-2">Package Name</th>
                        <th className="pt-2 pb-2">Pricing (GBP)</th>
                        <th className="pt-2 pb-2 text-center">Status</th>
                        <th className="pt-2 pb-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            <Preloader />
                          </td>
                        </tr>
                      ) : packages.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            No packages found
                          </td>
                        </tr>
                      ) : (
                        packages.map((pkg) => {
                          return (
                            <tr key={pkg.id}>
                              <td>
                                <strong>{pkg.pName}</strong>
                                {pkg.pDetail && (
                                  <div className="text-muted small">
                                    {pkg.pDetail}
                                  </div>
                                )}
                              </td>
                              <td>{formatPriceSummary(pkg)}</td>
                              <td className="text-center">
                                <StatusBadge
                                  isActive={pkg.pStatus}
                                  onClick={() => setStatusConfirmPackage(pkg)}
                                />
                              </td>
                              <td className="text-center">
                                <div className="btn-group" role="group">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleEditPackage(pkg)}
                                    title="Edit"
                                  >
                                    <i className="fa fa-edit"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDeletePackage(pkg.id)}
                                    title="Delete"
                                  >
                                    <i className="fa fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <p className="text-muted">
                      Showing {packages.length} package(s)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        title="Change package status?"
        subtitle={
          statusConfirmPackage
            ? `Are you sure you want to set "${statusConfirmPackage.pName || "this package"}" to ${
                statusConfirmPackage.pStatus ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmPackage(null)}
        isOpen={!!statusConfirmPackage}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmPackageStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManagePackages;

